/**
 * ConceptReferenceVisual - Renders an embedded concept reference
 *
 * Resolves the target concept via __resolved_concept in properties
 * and renders its drawings. Parent containers (PlayVisual) embed
 * resolved concept data in properties before passing to BlockRenderer.
 *
 * Pure visual component - no editor state or handlers.
 */

import { ConceptVisual } from './ConceptVisual'
import type { BlockVisualProps } from '../../Types'
import type { ConceptReferenceBlock } from '@/types/blocks/reference/ConceptReference'
import type { ConceptBlock } from '@/types/blocks/canvas/Concept'

export function ConceptReferenceVisual({ block }: BlockVisualProps) {
	// Type guard
	if (block.type !== 'concept_reference') {
		console.error('ConceptReferenceVisual called with non-concept_reference block:', block.type)
		return null
	}

	const refBlock = block as ConceptReferenceBlock

	// Get resolved concept data from properties (injected by data loader)
	const resolvedConcept = (refBlock.properties as Record<string, unknown>).__resolved_concept as
		| ConceptBlock
		| undefined

	// No resolved concept - show placeholder
	if (!resolvedConcept) {
		return (
			<g className="concept-reference-empty">
				<text
					x="0"
					y="0"
					fill="var(--muted-foreground)"
					fontSize="12"
					fontFamily="monospace"
				>
					Concept not resolved
				</text>
			</g>
		)
	}

	// Delegate rendering to ConceptVisual with the resolved concept block
	return <ConceptVisual block={resolvedConcept} />
}
